// Web App Development service page with scalability focus
import React from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { ServiceDetailPage } from '@/components/service-detail-page';
import { SEO } from '@/components/seo';

const webAppService = {
  name: "Web App Development",
  promise: "Scalable web applications built for growth and performance",
  primaryKPI: "99.9% uptime with 2.1s load times",
  signals: {
    bestFor: "Growing SaaS companies and platforms that need to scale",
    timeline: "6-12 weeks",
    team: "Senior full-stack developer + DevOps engineer",
    deliverables: "Production app + infrastructure + monitoring"
  },
  outputs: [
    "Modern React/Next.js or Vue.js application",
    "RESTful API with proper authentication",
    "Database design and optimization",
    "Cloud deployment (AWS/Vercel/Railway)",
    "CI/CD pipeline for automated deployments",
    "Performance monitoring and alerts",
    "Security audit and penetration testing",
    "Developer documentation and handoff"
  ],
  process: [
    {
      step: "Architecture & Planning",
      description: "Design scalable system architecture and technical roadmap",
      ownership: "We architect, you define business requirements",
      artifacts: ["System architecture", "Database schema", "API documentation"]
    },
    {
      step: "Core Development",
      description: "Build MVP with essential features and authentication",
      ownership: "We code, you review weekly demos",
      artifacts: ["Working MVP", "User authentication", "Core features"]
    },
    {
      step: "Scale & Optimize",
      description: "Add advanced features and optimize for performance",
      ownership: "We enhance, you test with real users",
      artifacts: ["Full feature set", "Performance optimization", "Load testing"]
    },
    {
      step: "Deploy & Monitor",
      description: "Production deployment with monitoring and support",
      ownership: "We deploy, you launch to users",
      artifacts: ["Live application", "Monitoring dashboard", "Launch support"]
    }
  ],
  proof: [
    {
      client: "B2B marketplace (Series B)",
      challenge: "Platform couldn't handle growing user base",
      result: "Rebuilt with microservices architecture and caching",
      metric: "10x increase in concurrent users"
    },
    {
      client: "Healthcare SaaS startup",
      challenge: "Slow dashboard affecting user retention",
      result: "Optimized queries and implemented real-time updates",
      metric: "+156% daily active user retention"
    }
  ],
  faqs: [
    {
      question: "What's the difference between a website and web app?",
      answer: "Web apps have user accounts, dynamic data, and complex interactions. Websites are mostly informational. We build both."
    },
    {
      question: "Which technology stack do you use?",
      answer: "We choose based on your needs: React/Next.js for most cases, Vue.js for certain projects, Node.js or Python for backends."
    },
    {
      question: "How do you ensure the app can scale?",
      answer: "We design with scalability from day one: proper database indexing, caching strategies, and cloud infrastructure that grows with you."
    },
    {
      question: "Do you handle ongoing maintenance?",
      answer: "Yes, we offer maintenance retainers starting at $2,500/month covering updates, monitoring, and feature additions."
    },
    {
      question: "How do you handle data security?",
      answer: "We implement encryption, secure authentication, regular security audits, and follow OWASP guidelines for all applications."
    },
    {
      question: "What if we need to make changes during development?",
      answer: "Minor changes are included. Major scope changes are discussed and estimated separately to keep the project on track."
    }
  ]
};

const WebAppDevelopment = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Web App Development - Scalable SaaS & Custom Applications | AffinityAim"
        description="Enterprise web app development with 99.9% uptime. React, Node.js, cloud infrastructure. Build scalable SaaS products in 6-12 weeks."
        canonical="https://www.affinityaim.com/services/web-app-development"
        keywords="web app development, SaaS development, custom web applications, React development"
        type="service"
      />
      <Navigation />
      <ServiceDetailPage service={webAppService} />
      <Footer />
    </div>
  );
};

export default WebAppDevelopment;